/**
 * FormFlu Registration Page
 * 
 * Handles the complete registration flow:
 * 1. User info + organization registration
 * 2. MFA setup (required for all users)
 * 3. Recovery code display
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts';
import './RegisterPage.css';

export function RegisterPage() {
  const navigate = useNavigate();
  const {
    step,
    isAuthenticated,
    isLoading,
    error,
    fieldErrors,
    mfaSetupData,
    recoveryCodes,
    register,
    startMfaSetup,
    completeMfaSetup,
    acknowledgeRecoveryCodes,
    clearError,
  } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  
  // Helper to get server-side field error
  const getServerFieldError = (field: string): string | null => {
    const errors = fieldErrors[field];
    return errors && errors.length > 0 ? errors[0] : null;
  };
  
  // Real-time validation errors (client-side)
  const getPasswordError = (): string | null => {
    // Server-side error takes precedence
    const serverError = getServerFieldError('password');
    if (serverError) return serverError;
    // Client-side validation
    if (!touched.password) return null;
    if (password.length === 0) return null;
    if (password.length < 12) return 'Password must be at least 12 characters';
    return null;
  };
  
  const getConfirmPasswordError = (): string | null => {
    if (!touched.confirmPassword) return null;
    if (confirmPassword.length === 0) return null;
    if (password !== confirmPassword) return 'Passwords do not match';
    return null;
  };
  
  // Combined errors (client + server)
  const passwordError = getPasswordError();
  const confirmPasswordError = getConfirmPasswordError();
  const emailError = getServerFieldError('email');
  const firstNameError = getServerFieldError('first_name');
  const lastNameError = getServerFieldError('last_name');
  const orgNameError = getServerFieldError('organization_name');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Auto-fetch MFA setup data when entering setup step
  useEffect(() => {
    if (step === 'mfa_setup' && !mfaSetupData) {
      startMfaSetup();
    }
  }, [step, mfaSetupData, startMfaSetup]);

  // Handle registration form submit
  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    // Validate password length (match backend requirement of 12 characters)
    if (password.length < 12) {
      setLocalError('Password must be at least 12 characters');
      return;
    }

    await register({
      email,
      password,
      first_name: firstName,
      last_name: lastName,
      organization_name: organizationName,
    });
  };

  // Handle MFA setup completion
  const handleMfaSetup = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    
    if (mfaSetupData) {
      await completeMfaSetup(mfaCode, mfaSetupData.secret);
    }
  };

  // Copy recovery codes to clipboard
  const handleCopyRecoveryCodes = async () => {
    const codesText = recoveryCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  // Render based on current step
  const renderContent = () => {
    switch (step) {
      case 'idle':
      case 'credentials':
        return (
          <form onSubmit={handleRegister} className="register-form">
            <div className="form-header">
              <Link to="/" className="logo-link">
                <img 
                  src="/images/finalformflumainlogo.png" 
                  alt="FormFlu - Back to Home" 
                  className="register-logo" 
                />
              </Link>
              <h1>Create your account</h1>
              <p>Start building HIPAA-compliant forms in minutes</p>
            </div>

            {(error || localError) && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span>
                {error || localError}
              </div>
            )}

            <div className="form-row">
              <div className={`form-group ${firstNameError ? 'has-error' : ''}`}>
                <label htmlFor="firstName">First name</label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  required
                  autoComplete="given-name"
                  autoFocus
                  className={firstNameError ? 'input-error' : ''}
                />
                {firstNameError && (
                  <span className="field-error">{firstNameError}</span>
                )}
              </div>

              <div className={`form-group ${lastNameError ? 'has-error' : ''}`}>
                <label htmlFor="lastName">Last name</label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  required
                  autoComplete="family-name"
                  className={lastNameError ? 'input-error' : ''}
                />
                {lastNameError && (
                  <span className="field-error">{lastNameError}</span>
                )}
              </div>
            </div>

            <div className={`form-group ${emailError ? 'has-error' : ''}`}>
              <label htmlFor="email">Email address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
                className={emailError ? 'input-error' : ''}
              />
              {emailError && (
                <span className="field-error">{emailError}</span>
              )}
            </div>

            <div className={`form-group ${orgNameError ? 'has-error' : ''}`}>
              <label htmlFor="organizationName">Organization name</label>
              <input
                type="text"
                id="organizationName"
                value={organizationName}
                onChange={(e) => setOrganizationName(e.target.value)}
                placeholder="Acme Healthcare"
                required
                className={orgNameError ? 'input-error' : ''}
              />
              {orgNameError ? (
                <span className="field-error">{orgNameError}</span>
              ) : (
                <span className="form-hint">This will be your workspace name</span>
              )}
            </div>

            <div className={`form-group ${passwordError ? 'has-error' : ''}`}>
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, password: true }))}
                placeholder="••••••••••••"
                required
                autoComplete="new-password"
                minLength={12}
                className={passwordError ? 'input-error' : ''}
              />
              {passwordError ? (
                <span className="field-error">{passwordError}</span>
              ) : (
                <span className="form-hint">Minimum 12 characters</span>
              )}
            </div>

            <div className={`form-group ${confirmPasswordError ? 'has-error' : ''}`}>
              <label htmlFor="confirmPassword">Confirm password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, confirmPassword: true }))}
                placeholder="••••••••"
                required
                autoComplete="new-password"
                className={confirmPasswordError ? 'input-error' : ''}
              />
              {confirmPasswordError && (
                <span className="field-error">{confirmPasswordError}</span>
              )}
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-full"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </button>

            <div className="register-footer">
              <p>Already have an account? <Link to="/login">Sign in</Link></p>
            </div>

            <div className="register-terms">
              <p>
                By creating an account, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </p>
            </div>
          </form>
        );

      case 'mfa_setup':
        return (
          <form onSubmit={handleMfaSetup} className="register-form mfa-setup-form">
            <div className="form-header">
              <div className="mfa-icon">📱</div>
              <h1>Set up two-factor authentication</h1>
              <p>
                Scan the QR code with your authenticator app
                (Google Authenticator, Authy, etc.)
              </p>
            </div>

            {error && (
              <div className="error-message" role="alert">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="hipaa-notice">
              <span className="notice-icon">🔒</span>
              <strong>HIPAA Requirement:</strong> Two-factor authentication is required 
              for all accounts to protect sensitive health information.
            </div>

            {mfaSetupData ? (
              <>
                <div className="qr-code-container">
                  <img 
                    src={mfaSetupData.qr_code} 
                    alt="Scan this QR code with your authenticator app"
                    className="qr-code"
                  />
                </div>

                <div className="manual-entry">
                  <p>Can't scan? Enter this code manually:</p>
                  <code className="secret-code">{mfaSetupData.secret}</code>
                </div>

                <div className="form-group">
                  <label htmlFor="setupCode">Enter verification code</label>
                  <input
                    type="text"
                    id="setupCode"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    required
                    autoComplete="one-time-code"
                    className="mfa-input"
                    maxLength={6}
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-full"
                  disabled={isLoading || mfaCode.length !== 6}
                >
                  {isLoading ? 'Verifying...' : 'Verify and enable'}
                </button>
              </>
            ) : (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading setup data...</p>
              </div>
            )}
          </form>
        );

      case 'recovery_codes':
        return (
          <div className="register-form recovery-codes-form">
            <div className="form-header">
              <div className="mfa-icon success">✅</div>
              <h1>Save your recovery codes</h1>
              <p>
                Store these codes in a safe place. You can use them to access
                your account if you lose your authenticator device.
              </p>
            </div>

            <div className="warning-message">
              <span className="warning-icon">⚠️</span>
              <strong>Important:</strong> These codes will only be shown once.
              Make sure to save them now!
            </div>

            <div className="recovery-codes-grid">
              {recoveryCodes.map((code, index) => (
                <code key={index} className="recovery-code">
                  {code}
                </code>
              ))}
            </div>

            <button 
              type="button" 
              className="btn btn-secondary btn-full"
              onClick={handleCopyRecoveryCodes}
            >
              {copiedCodes ? '✓ Copied!' : 'Copy all codes'}
            </button>

            <button 
              type="button" 
              className="btn btn-primary btn-full"
              onClick={acknowledgeRecoveryCodes}
            >
              I've saved my codes
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        {renderContent()}
      </div>
      
      <div className="register-background">
        <img 
          src="/images/finalformfluhealthylogo.png" 
          alt="" 
          className="background-mascot"
        />
      </div>
    </div>
  );
}

export default RegisterPage;
